import { Paperclip, X } from "lucide-react";
import { useState } from "react";

import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
interface LocalAttachment {
  id: string;
  file: File;
}

const MAX_UPLOAD_FILES = 5;
const MAX_UPLOAD_FILE_SIZE = 20 * 1024 * 1024;
const ACCEPTED_UPLOAD_TYPES =
  "image/*,application/pdf,text/plain,text/markdown,.md,.doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,.ppt,.pptx";

const getFileExtension = (fileName: string) => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : "";
};

const isAcceptedFileType = (file: File) =>
  ACCEPTED_UPLOAD_TYPES.split(",").some((accept) => {
    const acceptedType = accept.trim().toLowerCase();
    if (acceptedType.startsWith(".")) {
      return getFileExtension(file.name) === acceptedType;
    }
    if (acceptedType.endsWith("/*")) {
      return file.type
        .toLowerCase()
        .startsWith(acceptedType.replace("/*", "/"));
    }
    return file.type.toLowerCase() === acceptedType;
  });
const BlueprintAttachments = () => {
  const { t } = useTranslation();

  const [attachments, setAttachments] = useState<LocalAttachment[]>([]);
  const [fileInputKey, setFileInputKey] = useState(0);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) return;

    const remainingSlots = MAX_UPLOAD_FILES - attachments.length;
    if (selectedFiles.length > remainingSlots) {
      toast.error(t("playground.fileUploadLimitExceeded"));
    }

    const nextAttachments: LocalAttachment[] = [];
    selectedFiles.slice(0, remainingSlots).forEach((file) => {
      const duplicate = [...attachments, ...nextAttachments].some(
        ({ file: existingFile }) =>
          existingFile.name.trim().toLowerCase() ===
            file.name.trim().toLowerCase() &&
          existingFile.size === file.size &&
          existingFile.type.toLowerCase() === file.type.toLowerCase(),
      );

      if (duplicate) {
        toast.error(
          t("playground.duplicateFileUpload", { fileName: file.name }),
        );
        return;
      }
      if (file.size > MAX_UPLOAD_FILE_SIZE) {
        toast.error(t("playground.fileSizeExceedsLimit"));
        return;
      }
      if (!isAcceptedFileType(file)) {
        toast.error(t("playground.unsupportedFileType"));
        return;
      }

      nextAttachments.push({
        id: `${file.name}-${file.size}-${file.lastModified}`,
        file,
      });
    });

    setAttachments((current) => [...current, ...nextAttachments]);
    setFileInputKey((current) => current + 1);
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div>
          <Typography
            variant="body2"
            className="text-foreground text-sm font-semibold"
          >
            {t("playground.uploadDocuments", "Upload Documents")}
          </Typography>
          <Typography variant="body2" className="text-sm text-muted-foreground">
            {t("playground.uploadDocumentTooltip")}
          </Typography>
        </div>

        <label
          htmlFor={`blueprint-file-upload-${fileInputKey}`}
          className="cursor-pointer rounded-[35px] p-2 text-foreground transition-all duration-150 hover:bg-secondary/50"
          title={t("playground.uploadDocumentTooltip")}
        >
          <Paperclip className="size-5" />
          <span className="sr-only">
            {t("playground.uploadDocumentTooltip")}
          </span>
          <input
            key={fileInputKey}
            id={`blueprint-file-upload-${fileInputKey}`}
            type="file"
            className="hidden"
            accept={ACCEPTED_UPLOAD_TYPES}
            multiple
            onChange={handleFileChange}
          />
        </label>
      </div>
      <div className="flex min-h-10 w-full flex-wrap gap-2 rounded-[8px] border border-border bg-input p-2">
        {attachments.length ? (
          attachments.map(({ id, file }) => (
            <span
              key={id}
              className="flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-xs text-foreground"
            >
              {file.name}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-4 rounded-full p-0"
                onClick={() =>
                  setAttachments((current) =>
                    current.filter((item) => item.id !== id),
                  )
                }
                aria-label={`Remove ${file.name}`}
              >
                <X className="size-3" />
              </Button>
            </span>
          ))
        ) : (
          <span className="text-sm text-muted-foreground">
            No files selected
          </span>
        )}
      </div>
    </div>
  );
};

export default BlueprintAttachments;
